import classnames from 'classnames/bind';
import { Controller, useForm } from 'react-hook-form';

import { Gateway } from 'types/gateway';

import { useUpdateGateway } from 'gateways/hooks/useUpdateGateway';

import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { Field } from 'components/Field/Field';
import { InlineNotification } from 'components/InlineNotification/InlineNotification';
import { NumberInput } from 'components/NumberInput/NumberInput';

import styles from './GatewayDetail.module.scss';

const c = classnames.bind(styles);

type GatewayDetailSettingsProps = {
  gateway: Gateway;
};

type GatewaySettingsFieldValues = {
  max_running_deployments: number;
  max_deployments_per_process: number;
  onvif_request_timeout_secs: number;
};

export function GatewayDetailSettings({ gateway }: GatewayDetailSettingsProps) {
  const { formState, control, handleSubmit, reset } =
    useForm<GatewaySettingsFieldValues>({
      defaultValues: {
        max_running_deployments: gateway.max_running_deployments,
        max_deployments_per_process: gateway.max_deployments_per_process,
        onvif_request_timeout_secs: gateway.onvif_request_timeout_secs ?? 30,
      },
    });

  const { mutate: updateGateway, isLoading: isSaving } = useUpdateGateway({
    onSuccess(gateway) {
      reset({
        max_running_deployments: gateway.max_running_deployments,
        max_deployments_per_process: gateway.max_deployments_per_process,
        onvif_request_timeout_secs: gateway.onvif_request_timeout_secs ?? 30,
      });
    },
  });

  async function onSubmit({
    max_running_deployments,
    max_deployments_per_process,
    onvif_request_timeout_secs,
  }: GatewaySettingsFieldValues) {
    updateGateway({
      id: gateway.id,
      max_running_deployments,
      max_deployments_per_process,
      onvif_request_timeout_secs,
    });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={c('settings')}>
      <Field
        id="max_deployments"
        label="Max. number of running deployments"
        required
      >
        <Controller
          control={control}
          name="max_running_deployments"
          render={({ field: { value, onChange } }) => (
            <NumberInput
              min={0}
              max={65_535}
              value={value}
              onChange={onChange}
            />
          )}
          defaultValue={10}
          rules={{
            required: 'Please enter a valid number.',
            min: {
              value: 0,
              message: 'Please enter a value above 0.',
            },
          }}
        />
      </Field>

      <Field
        id="max_deployments_per_process"
        label="Workflows per process"
        info="Adjusting this parameter impacts memory and CPU utilization. Only do so if instructed by support."
        required
      >
        <Controller
          control={control}
          name="max_deployments_per_process"
          render={({ field: { value, onChange } }) => (
            <NumberInput
              min={0}
              max={65_535}
              value={value}
              onChange={onChange}
            />
          )}
          defaultValue={10}
          rules={{
            required: 'Please enter a valid number.',
            min: {
              value: 0,
              message: 'Please enter a value above 0.',
            },
          }}
        />
      </Field>

      <Field
        id="onvif_request_timeout_secs"
        label="ONVIF request timeout (seconds)"
        info="Timeout for ONVIF requests. Increase this value for VMS systems with many cameras. Only adjust if instructed by support."
        required
      >
        <Controller
          control={control}
          name="onvif_request_timeout_secs"
          render={({ field: { value, onChange } }) => (
            <NumberInput min={1} max={600} value={value} onChange={onChange} />
          )}
          defaultValue={30}
          rules={{
            required: 'Please enter a valid number.',
            min: {
              value: 1,
              message: 'Please enter a value of at least 1 second.',
            },
          }}
        />
      </Field>

      <ButtonGroup>
        <Button type="submit" variant="primary" loading={isSaving}>
          Save
        </Button>
        {!isSaving && !formState.isDirty && formState.isSubmitSuccessful && (
          <InlineNotification intent="success" icon="check-circle">
            Saved!
          </InlineNotification>
        )}
      </ButtonGroup>
    </form>
  );
}
